/* eslint-disable no-underscore-dangle */
/* eslint-disable react/require-default-props */
/* eslint-disable react/no-danger */
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Paper from '@material-ui/core/Paper';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import Pagination from '@material-ui/lab/Pagination';
import { navigate } from '@reach/router';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import HTMLEllipsis from 'react-lines-ellipsis/lib/html';

import ExternalLink from '../components/external-link';
import SEO from '../components/seo';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.mixins.toolbar.minHeight,
    padding: theme.spacing(5, 0)
  },
  container: {
    maxWidth: 900,
    padding: theme.spacing(10, 0),
    margin: '0 auto',
    backgroundColor: theme.palette.common.white,
    '& a': {
      display: 'block',
      textDecoration: 'none'
    }
  },
  title: {
    marginBottom: theme.spacing(7),
    fontSize: 36,
    textTransform: 'uppercase',
    lineHeight: 1.27
  },
  list: {
    border: 'none'
  },
  listItem: {
    marginBottom: theme.spacing(6),
    '&:last-of-type': {
      marginBottom: 0
    },
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4)
    }
  },
  wrap: {
    width: '100%',
    padding: theme.spacing(3)
  },
  articleDate: {
    marginBottom: theme.spacing(4)
  },
  articleImg: {
    width: '100%',
    height: 'auto',
    borderRadius: theme.shape.borderRadius
  },
  articleTitle: {
    display: 'block',
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.primary.dark
    }
  },
  articleText: {
    marginBottom: theme.spacing(5),
    display: '-webkit-box',
    maxHeight: 'calc(1rem * 1.9 * 3)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '-webkit-line-clamp': 3,
    '-webkit-box-orient': 'vertical',
    [theme.breakpoints.up('md')]: {
      maxHeight: 'calc(1rem * 2.22 * 2)',
      '-webkit-line-clamp': 2
    }
  },
  articleOutboundLink: {
    color: theme.palette.primary.main
  },
  pagination: {
    margin: theme.spacing(6, 0),
    '& > ul': {
      justifyContent: 'center'
    }
  }
}));

const placeholderImg = 'https://via.placeholder.com/200x180.png';

/**
 * HOC which creates an external or internal link
 */
function makePostLink(post = {}) {
  const { meta } = post;

  const PostLink = React.forwardRef(function PostLink(
    { children, ...props },
    ref
  ) {
    if (meta && meta._links_to) {
      return (
        <ExternalLink
          ref={ref}
          link={meta._links_to}
          target={meta._links_to_target}
          {...props}
        >
          {children}
        </ExternalLink>
      );
    }

    return (
      <Link ref={ref} to={`media-and-blogs/${post.slug}`} {...props}>
        {children}
      </Link>
    );
  });

  PostLink.propTypes = {
    children: PropTypes.node
  };

  return PostLink;
}

export default function BlogTemplate({ pageContext = {} }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { group: posts, index: page, pageCount } = pageContext;

  return (
    <section className={classes.root}>
      <SEO title={t('blogPage.pageTitle')} />
      <Container maxWidth="xl">
        <div className={classes.container}>
          <Typography className={classes.title} variant="h1">
            {t('blogPage.pageTitle')}
          </Typography>
          <List className={classes.list} aria-label="blog-articles">
            {posts.map(({ node: post }) => {
              const PostLink = makePostLink(post);
              const video = post.categories.find(
                category => category.slug === 'video'
              );

              return (
                <ListItem
                  key={post.slug}
                  className={classes.listItem}
                  alignItems="flex-start"
                >
                  <Paper className={classes.wrap}>
                    <Grid container spacing={6}>
                      <Grid item xs={12} sm={4} md={3}>
                        <Box component={PostLink}>
                          <img
                            className={classes.articleImg}
                            src={
                              post.featured_media
                                ? post.featured_media.localFile.childImageSharp
                                    .fluid.src
                                : placeholderImg
                            }
                            alt={post.slug}
                            title="Contemplative Reptile"
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={8} md={9}>
                        <div className={classes.articleDate}>{post.date}</div>
                        <Box
                          className={classes.articleTitle}
                          component={PostLink}
                        >
                          <Typography variant="h4" color="textPrimary">
                            {post.title}
                          </Typography>
                        </Box>
                        <Typography
                          className={classes.articleText}
                          variant="body1"
                          component="div"
                        >
                          <HTMLEllipsis
                            unsafeHTML={post.excerpt}
                            maxLine="2"
                            ellipsis="..."
                            basedOn="letters"
                          />
                        </Typography>
                        <Box component={PostLink}>
                          <Button variant="contained" color="primary">
                            {video
                              ? t('blogPage.watchNow')
                              : t('blogPage.readMore')}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </ListItem>
              );
            })}
          </List>
        </div>
        {pageCount > 1 && (
          <Pagination
            className={classes.pagination}
            count={pageCount}
            page={page}
            shape="rounded"
            color="primary"
            onChange={(_, newPage) => {
              navigate(`/media-and-blogs/${newPage === 1 ? '' : newPage}`);
            }}
          />
        )}
      </Container>
    </section>
  );
}

BlogTemplate.propTypes = {
  pageContext: PropTypes.shape({
    group: PropTypes.arrayOf(PropTypes.shape({})),
    index: PropTypes.number.isRequired,
    pageCount: PropTypes.number.isRequired
  })
};
